import type { ReactNode } from 'react';

export interface TaxInvoiceTaxRow {
  taxTypeCode: string;
  ratePct: number;
  taxableAmount: number;
  taxAmount: number;
}

export interface TaxInvoiceDoc {
  invoice: {
    claimNumber: string;
    invoiceNumber: string;
    invoiceDate: string;
    billingPeriodFrom: string;
    billingPeriodTo: string;
    status: string;
    remarks: string;
  };
  supplier: {
    code: string;
    name: string;
    legalName: string;
    address: string;
    pincode: string;
    gstin: string;
    pan: string;
    mobile: string;
    email: string;
  };
  buyer: {
    legalName: string;
    tradeName: string;
    address: string;
    city: string;
    pincode: string;
    gstin: string;
  };
  placeOfSupply: string;
  supplyType: 'INTRA_STATE' | 'INTER_STATE';
  lines: ReadonlyArray<{
    serial: number;
    lineId: string;
    jobNumber: string;
    workOrderNumber: string;
    dealerName: string;
    location: string;
    workTypeCode: string;
    description: string;
    /** Frozen on the bill line from the work order it bills against. */
    hsnSacCode?: string;
    brandingTypeName?: string;
    brandingItemName?: string;
    servicePeriod?: string;
    dimensions: string;
    quantity: number;
    uomCode: string;
    areaSqft: number;
    ratePerUnit: number;
    basicAmount: number;
    taxAmount: number;
    totalAmount: number;
    remarks: string;
    taxes: readonly TaxInvoiceTaxRow[];
  }>;
  expenses: ReadonlyArray<{
    expenseId: string;
    expenseTypeName: string;
    amount: number;
    remarks: string;
  }>;
  taxSummary: readonly TaxInvoiceTaxRow[];
  totals: {
    basicAmount: number;
    totalTax: number;
    grandTotal: number;
    amountInWords: string;
  };
}

const inr = (n: number): string =>
  `\u20B9${(n ?? 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const cell: React.CSSProperties = {
  border: '1px solid #333',
  padding: '6px 8px',
  fontSize: 12,
  verticalAlign: 'top',
};
const head: React.CSSProperties = {
  ...cell,
  background: '#eef2f7',
  fontWeight: 600,
  textAlign: 'left',
};
const num: React.CSSProperties = { ...cell, textAlign: 'right' };
const muted: React.CSSProperties = { color: '#555' };

/** The printable tax invoice. Shared so the vendor and the back office read the
 *  same document, never two drifting versions of it. */
export function TaxInvoice({ doc }: { doc: TaxInvoiceDoc }): ReactNode {
  const { invoice, supplier, buyer, totals } = doc;

  return (
    <div
      style={{
        maxWidth: 920,
        margin: '0 auto',
        background: '#fff',
        border: '1px solid #333',
        fontFamily: 'Segoe UI, Arial, sans-serif',
        color: '#111',
      }}
    >
      <div
        style={{
          textAlign: 'center',
          padding: '10px 0',
          borderBottom: '1px solid #333',
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: 1 }}>
          TAX INVOICE
        </div>
        <div style={{ fontSize: 11 }}>
          {invoice.status === 'DRAFT'
            ? 'DRAFT \u2014 NOT YET SUBMITTED'
            : invoice.status}
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td style={{ ...cell, width: '50%' }}>
              <strong>Supplier (From)</strong>
              <div>{supplier.legalName}</div>
              {supplier.name !== supplier.legalName && <div>{supplier.name}</div>}
              <div>
                {supplier.address}
                {supplier.pincode ? ` \u2013 ${supplier.pincode}` : ''}
              </div>
              <div>GSTIN: {supplier.gstin || '\u2014'}</div>
              <div>PAN: {supplier.pan || '\u2014'}</div>
              <div style={muted}>
                {supplier.mobile} {supplier.email}
              </div>
              <div style={muted}>Vendor Code: {supplier.code}</div>
            </td>
            <td style={{ ...cell, width: '50%' }}>
              <strong>Buyer (To)</strong>
              <div>{buyer.legalName || '\u2014'}</div>
              <div>{buyer.tradeName}</div>
              <div>
                {buyer.address}
                {buyer.city ? `, ${buyer.city}` : ''}
                {buyer.pincode ? ` \u2013 ${buyer.pincode}` : ''}
              </div>
              <div>GSTIN: {buyer.gstin || '\u2014'}</div>
            </td>
          </tr>
          <tr>
            <td style={cell}>
              <div>
                <strong>Invoice No:</strong> {invoice.invoiceNumber || '\u2014'}
              </div>
              <div>
                <strong>Invoice Date:</strong> {invoice.invoiceDate || '\u2014'}
              </div>
              <div>
                <strong>Claim No:</strong> {invoice.claimNumber}
              </div>
            </td>
            <td style={cell}>
              <div>
                <strong>Place of Supply:</strong> {doc.placeOfSupply || '\u2014'}
              </div>
              <div>
                <strong>Supply Type:</strong>{' '}
                {doc.supplyType === 'INTRA_STATE'
                  ? 'Intra-State (CGST + SGST)'
                  : 'Inter-State (IGST)'}
              </div>
              <div>
                <strong>Billing Period:</strong> {invoice.billingPeriodFrom} to{' '}
                {invoice.billingPeriodTo}
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={head}>#</th>
            <th style={head}>Description of Service</th>
            <th style={head}>HSN / SAC</th>
            <th style={head}>Work Order / Job</th>
            <th style={head}>Dealer / Site</th>
            <th style={{ ...head, textAlign: 'right' }}>Qty</th>
            <th style={{ ...head, textAlign: 'right' }}>Rate</th>
            <th style={{ ...head, textAlign: 'right' }}>Taxable Value</th>
          </tr>
        </thead>
        <tbody>
          {doc.lines.length === 0 && (
            <tr>
              <td style={cell} colSpan={8}>
                No lines on this bill yet.
              </td>
            </tr>
          )}
          {doc.lines.map(l => (
            <tr key={l.lineId}>
              <td style={cell}>{l.serial}</td>
              <td style={cell}>
                <div>{l.description || l.workTypeCode}</div>
                {l.brandingTypeName && (
                  <div style={muted}>
                    {l.brandingTypeName}
                    {l.brandingItemName ? ` \u2014 ${l.brandingItemName}` : ''}
                  </div>
                )}
                {l.servicePeriod && <div style={muted}>Period: {l.servicePeriod}</div>}
                {l.dimensions && <div style={muted}>Dimensions: {l.dimensions}</div>}
                {l.remarks && <div style={muted}>Remark: {l.remarks}</div>}
              </td>
              <td style={cell}>{l.hsnSacCode || '\u2014'}</td>
              <td style={cell}>
                <div>{l.workOrderNumber || '\u2014'}</div>
                <div style={muted}>{l.jobNumber}</div>
              </td>
              <td style={cell}>
                <div>{l.dealerName}</div>
                <div style={muted}>{l.location}</div>
              </td>
              <td style={num}>
                {l.areaSqft || l.quantity} {l.uomCode}
              </td>
              <td style={num}>{inr(l.ratePerUnit)}</td>
              <td style={num}>{inr(l.basicAmount)}</td>
            </tr>
          ))}
          {doc.expenses.map(e => (
            <tr key={e.expenseId}>
              <td style={cell} />
              <td style={cell} colSpan={6}>
                {e.expenseTypeName || 'Other expense'}
                {e.remarks ? ` \u2014 ${e.remarks}` : ''}
              </td>
              <td style={num}>{inr(e.amount)}</td>
            </tr>
          ))}
          <tr>
            <td style={{ ...cell, fontWeight: 600 }} colSpan={7}>
              Total Taxable Value
            </td>
            <td style={{ ...num, fontWeight: 600 }}>{inr(totals.basicAmount)}</td>
          </tr>
        </tbody>
      </table>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={head}>Tax</th>
            <th style={{ ...head, textAlign: 'right' }}>Rate %</th>
            <th style={{ ...head, textAlign: 'right' }}>Taxable Value</th>
            <th style={{ ...head, textAlign: 'right' }}>Tax Amount</th>
          </tr>
        </thead>
        <tbody>
          {doc.taxSummary.length === 0 && (
            <tr>
              <td style={cell} colSpan={4}>
                Tax not calculated yet \u2014 run Calculate before submitting.
              </td>
            </tr>
          )}
          {doc.taxSummary.map(t => (
            <tr key={`${t.taxTypeCode}-${t.ratePct}`}>
              <td style={cell}>{t.taxTypeCode}</td>
              <td style={num}>{t.ratePct}</td>
              <td style={num}>{inr(t.taxableAmount)}</td>
              <td style={num}>{inr(t.taxAmount)}</td>
            </tr>
          ))}
          <tr>
            <td style={{ ...cell, fontWeight: 600 }} colSpan={3}>
              Total Tax
            </td>
            <td style={{ ...num, fontWeight: 600 }}>{inr(totals.totalTax)}</td>
          </tr>
          <tr>
            <td style={{ ...cell, fontWeight: 700 }} colSpan={3}>
              Grand Total
            </td>
            <td style={{ ...num, fontWeight: 700 }}>{inr(totals.grandTotal)}</td>
          </tr>
        </tbody>
      </table>

      <div style={{ ...cell, borderTop: 'none' }}>
        <strong>Amount in words:</strong> {totals.amountInWords}
      </div>
      {invoice.remarks && (
        <div style={{ ...cell, borderTop: 'none' }}>
          <strong>Remarks:</strong> {invoice.remarks}
        </div>
      )}
      <div
        style={{
          ...cell,
          borderTop: 'none',
          display: 'flex',
          justifyContent: 'space-between',
          paddingTop: 36,
        }}
      >
        <span>Receiver&apos;s Signature</span>
        <span>For {supplier.legalName}</span>
      </div>
    </div>
  );
}
